import { Waves, Wind, Tornado, Zap, Layers, Download } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { SCENARIOS } from "@/lib/constants";

const ICONS = { flooding: Waves, erosion: Wind, hurricane: Tornado };

export default function SimulatorPanel({
  scenario,
  setScenario,
  intensity,
  setIntensity,
  slr,
  setSlr,
  baseLayer,
  setBaseLayer,
  onExport,
}) {
  return (
    <div data-testid="simulator-panel" className="glass rounded-lg p-4 flex flex-col gap-5 h-full overflow-y-auto scan-line">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-cyan" />
          <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">
            Stress Simulator
          </h2>
        </div>
        <p className="font-mono-t text-[10px] text-slate-500 uppercase tracking-wider">
          Physics-based hazard modelling
        </p>
      </div>

      {/* Hazard tabs */}
      <div>
        <label className="font-mono-t text-[10px] uppercase tracking-widest text-slate-400">Hazard Vector</label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {Object.entries(SCENARIOS).map(([key, s]) => {
            const Icon = ICONS[key];
            const active = scenario === key;
            return (
              <button
                key={key}
                data-testid={`scenario-${key}`}
                onClick={() => setScenario(key)}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-md border transition-all duration-200 ${
                  active
                    ? "bg-cyan/15 border-cyan text-cyan pulse-glow"
                    : "bg-slate-900/60 border-border text-slate-400 hover:border-slate-600 hover:text-slate-200"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-heading text-xs font-semibold uppercase tracking-wide">{s.label}</span>
              </button>
            );
          })}
        </div>
        <p className="font-mono-t text-[10px] text-slate-500 mt-2 leading-relaxed">
          {SCENARIOS[scenario].desc}
        </p>
      </div>

      {/* Intensity */}
      <div>
        <div className="flex items-center justify-between">
          <label className="font-mono-t text-[10px] uppercase tracking-widest text-slate-400">Storm surge (what-if)</label>
          <span data-testid="surge-value" className="font-mono-t text-sm font-bold text-cyan">+{(intensity * 4).toFixed(2)} m</span>
        </div>
        <Slider
          data-testid="intensity-slider"
          value={[intensity * 100]}
          onValueChange={(v) => setIntensity(v[0] / 100)}
          max={100}
          step={1}
          className="mt-3"
        />
        <div className="font-mono-t text-[9px] text-slate-600 mt-1">Added on top of the still-water level · live mode uses the measured gauge level</div>
      </div>

      {/* Sea level rise */}
      <div>
        <div className="flex items-center justify-between">
          <label className="font-mono-t text-[10px] uppercase tracking-widest text-slate-400">
            Still-water / sea-level
          </label>
          <span className="font-mono-t text-sm font-bold text-cyan">{slr.toFixed(2)} m</span>
        </div>
        <Slider
          data-testid="slr-slider"
          value={[slr * 10]}
          onValueChange={(v) => setSlr(v[0] / 10)}
          max={30}
          step={1}
          className="mt-3"
        />
        <div className="flex justify-between font-mono-t text-[9px] text-slate-600 mt-1">
          <span>0m</span>
          <span>IPCC 2100 · 1m</span>
          <span>3m</span>
        </div>
      </div>

      {/* Base layer */}
      <div>
        <label className="font-mono-t text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5" /> Basemap
        </label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {[
            { k: "satellite", l: "Satellite" },
            { k: "dark", l: "Tactical Dark" },
          ].map((o) => (
            <button
              key={o.k}
              data-testid={`basemap-${o.k}`}
              onClick={() => setBaseLayer(o.k)}
              className={`py-2 rounded-md border text-xs font-heading uppercase tracking-wide transition-all ${
                baseLayer === o.k
                  ? "bg-cyan/15 border-cyan text-cyan"
                  : "bg-slate-900/60 border-border text-slate-400 hover:text-slate-200"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-auto pt-2">
        <Button
          data-testid="export-ledger-button"
          onClick={onExport}
          variant="outline"
          className="w-full border-cyan/40 text-cyan hover:bg-cyan/10 hover:text-cyan font-heading uppercase tracking-wide text-xs"
        >
          <Download className="w-4 h-4 mr-2" /> Export Readiness Ledger
        </Button>
      </div>
    </div>
  );
}
